<template>
  <div class="activity-detail-container">
    <div class="page-header">
      <el-page-header @back="goBack" content="活动详情"></el-page-header>
    </div>

    <div v-loading="loading" style="margin-top: 16px;">
      <template v-if="detailData">
        <el-card shadow="never" class="detail-card">
          <div class="detail-header">
            <div class="detail-cover" v-if="detailData.coverImage">
              <el-image :src="resolveUrl(detailData.coverImage)" fit="cover" style="width: 100%; height: 240px; border-radius: 8px;"></el-image>
            </div>
            <div class="detail-title-section">
              <h1 class="detail-title">{{ detailData.title }}</h1>
              <p class="detail-subtitle" v-if="detailData.subtitle">{{ detailData.subtitle }}</p>
              <div class="detail-meta">
                <el-tag :type="getCategoryTagType(detailData.category)" size="small">{{ getCategoryLabel(detailData.category) }}</el-tag>
                <el-tag :type="getStatusType(detailData.status)" size="small" style="margin-left: 8px;">{{ getStatusLabel(detailData.status) }}</el-tag>
                <span class="meta-item" v-if="detailData.isTop"><i class="el-icon-star-on"></i> 置顶展示</span>
              </div>
            </div>
          </div>

          <el-descriptions :column="2" border size="medium" style="margin-top: 20px;">
            <el-descriptions-item label="活动ID">{{ detailData.id }}</el-descriptions-item>
            <el-descriptions-item label="活动分类">{{ getCategoryLabel(detailData.category) }}</el-descriptions-item>
            <el-descriptions-item label="活动状态">
              <el-tag :type="getStatusType(detailData.status)" size="small">{{ getStatusLabel(detailData.status) }}</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="置顶展示">{{ detailData.isTop ? '是' : '否' }}</el-descriptions-item>
            <el-descriptions-item label="活动开始时间">{{ formatDate(detailData.activityStartTime) }}</el-descriptions-item>
            <el-descriptions-item label="活动结束时间">{{ formatDate(detailData.activityEndTime) }}</el-descriptions-item>
            <el-descriptions-item label="活动地点" :span="2">{{ detailData.activityLocation || '-' }}</el-descriptions-item>
            <el-descriptions-item label="活动链接" :span="2">
              <el-link v-if="detailData.activityLink" :href="detailData.activityLink" target="_blank" type="primary">{{ detailData.activityLink }}</el-link>
              <span v-else>-</span>
            </el-descriptions-item>
            <el-descriptions-item label="组织者">{{ detailData.organizer || '-' }}</el-descriptions-item>
            <el-descriptions-item label="联系方式">{{ detailData.contactInfo || '-' }}</el-descriptions-item>
            <el-descriptions-item label="最大参与人数">{{ detailData.maxParticipants || '不限' }}</el-descriptions-item>
            <el-descriptions-item label="当前参与人数">{{ detailData.currentParticipants || 0 }}</el-descriptions-item>
            <el-descriptions-item label="浏览次数">{{ detailData.viewCount || 0 }}</el-descriptions-item>
            <el-descriptions-item label="点赞数">{{ detailData.likeCount || 0 }}</el-descriptions-item>
            <el-descriptions-item label="收藏数">{{ detailData.favoriteCount || 0 }}</el-descriptions-item>
            <el-descriptions-item label="标签" :span="2">
              <template v-if="detailData.tags">
                <el-tag v-for="tag in detailData.tags.split(',')" :key="tag" size="mini" style="margin-right: 4px;">{{ tag.trim() }}</el-tag>
              </template>
              <span v-else>-</span>
            </el-descriptions-item>
            <el-descriptions-item label="发布时间">{{ formatDate(detailData.publishTime) }}</el-descriptions-item>
            <el-descriptions-item label="创建者">{{ detailData.creatorName || detailData.creator || '-' }}</el-descriptions-item>
            <el-descriptions-item label="创建时间">{{ formatDate(detailData.createDate) }}</el-descriptions-item>
            <el-descriptions-item label="更新时间">{{ formatDate(detailData.updateDate) }}</el-descriptions-item>
            <el-descriptions-item label="备注" :span="2">{{ detailData.remark || '-' }}</el-descriptions-item>
          </el-descriptions>
        </el-card>

        <el-card shadow="never" class="detail-card" style="margin-top: 16px;" v-if="detailData.content">
          <div slot="header"><strong>活动内容</strong></div>
          <div class="detail-content" v-html="detailData.content"></div>
        </el-card>

        <el-card shadow="never" class="detail-card" style="margin-top: 16px;" v-if="detailData.images && detailData.images.length > 0">
          <div slot="header"><strong>活动图片 ({{ detailData.images.length }})</strong></div>
          <div class="detail-images">
            <div class="detail-image-item" v-for="(img, idx) in detailData.images" :key="idx">
              <el-image
                :src="resolveUrl(img.imageUrl)"
                fit="cover"
                :preview-src-list="detailData.images.map(i => resolveUrl(i.imageUrl))"
                style="width: 100%; height: 160px; border-radius: 8px; border: 1px solid #ebeef5;"
              ></el-image>
              <p class="image-caption" v-if="img.caption">{{ img.caption }}</p>
            </div>
          </div>
        </el-card>

        <div class="detail-actions">
          <el-button @click="goBack">返回列表</el-button>
          <el-button type="primary" @click="handleEdit" v-if="hasPermission('activity:community:update')">编辑活动</el-button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { getActivityDetail } from '@/apis/activity/activity'
import { getDictDataByType } from '@/apis/system/dict'
import { hasPermission } from '@/utils/permission'
import { getFullUrl } from '@/utils'

export default {
  name: 'CommunityDetail',
  data() {
    return {
      loading: false,
      detailData: null,
      categoryMap: {}
    }
  },
  created() {
    this.loadCategoryDict()
    const id = this.$route.params.id
    if (id) {
      this.loadDetail(id)
    }
  },
  methods: {
    hasPermission,
    resolveUrl(url) {
      return getFullUrl(url)
    },
    loadCategoryDict() {
      getDictDataByType('ACTIVITY_CATEGORY').then(res => {
        const data = res || []
        this.categoryMap = {}
        data.forEach(item => {
          this.categoryMap[item.key] = item.name
        })
      }).catch(e => {
        console.error('加载活动分类字典失败:', e)
      })
    },
    goBack() {
      this.$router.push('/activity/community')
    },
    loadDetail(id) {
      this.loading = true
      getActivityDetail(id).then(res => {
        if (res) {
          this.detailData = res
        }
      }).catch(e => {
        console.error('加载活动详情失败:', e)
      }).finally(() => {
        this.loading = false
      })
    },
    handleEdit() {
      this.$router.push(`/activity/community/edit/${this.detailData.id}`)
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    },
    getCategoryLabel(category) {
      return this.categoryMap[category] || category || '-'
    },
    getCategoryTagType(category) {
      const map = { ONLINE: '', OFFLINE: 'success', PROMOTION: 'warning', COMMUNITY: 'info', OTHER: 'info' }
      return map[category] || 'info'
    },
    getStatusLabel(status) {
      const map = { DRAFT: '草稿', PUBLISHED: '已发布', ARCHIVED: '已归档', CANCELED: '已取消' }
      return map[status] || status
    },
    getStatusType(status) {
      const map = { DRAFT: 'info', PUBLISHED: 'success', ARCHIVED: 'warning', CANCELED: 'danger' }
      return map[status] || 'info'
    }
  }
}
</script>

<style scoped>
.activity-detail-container {
  padding: 20px;
}

.page-header {
  background: #fff;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.detail-card >>> .el-card__body {
  padding: 24px;
}

.detail-header {
  text-align: center;
}

.detail-cover {
  margin-bottom: 20px;
}

.detail-title {
  font-size: 24px;
  font-weight: 700;
  color: #303133;
  margin: 0 0 8px;
}

.detail-subtitle {
  font-size: 16px;
  color: #606266;
  margin: 0 0 12px;
}

.detail-meta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-bottom: 8px;
}

.meta-item {
  font-size: 13px;
  color: #909399;
  margin-left: 12px;
}

.meta-item i {
  color: #e6a23c;
}

.detail-content {
  line-height: 1.8;
  font-size: 15px;
  color: #303133;
  word-break: break-word;
}

.detail-content >>> img {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  margin: 8px 0;
}

.detail-images {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
}

.detail-image-item {
  text-align: center;
}

.image-caption {
  font-size: 13px;
  color: #909399;
  margin-top: 6px;
}

.detail-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
}
</style>
